import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
// component to connect
import { AppScreenings } from '@unite-us/app-screenings';
import ClientsDrawer from 'src/components/ClientsDrawer';

// utils
import { isCaseManager } from 'src/components/User/utils';
import { env } from 'src/config/env';
import { TrackerContext } from '@unite-us/client-utils';
import Notifier from 'common/helpers/Notifier';
import { connect } from 'react-redux';
import { browserHistory } from 'common/utils/browserHistory';
import * as apiHooks from 'src/api/APIHooks';
import { useFindAllPlan } from 'src/api/core/Plans/planHooks';
import fetchInsurances from 'src/api/core/Insurances/fetchInsurances';
import {
  MethodsOfContact,
  VoiceMailOk,
} from 'src/components/Facesheet/Profile/ContactRequirements/components';
import StatusSelect from 'src/components/StatusSelect';
import {
  generateClientData,
  getDobData,
  formatAddress,
  formatHouseholdCount,
  filterMilitaryInfo,
  formatDemographicInfo,
} from 'src/components/ClientSummary/utils';
import findPrimaryOrFirst from 'src/components/Contacts/utils/findPrimaryOrFirst';
import { dollarAmount } from 'common/display/Money/DollarAmount';
import {
  crtb1121HideFieldInForm,
  scrn468ScreeningTimeTracking,
  hbh1509ClientsDrawer,
  hbh1025ShowEligibilityAssessmentTab,
  scrn942ScreeningsResultsUpdates,
} from 'common/utils/FeatureFlags/flags';
import { BackButton } from 'common/buttons';

import { coreApi, getAuthToken } from 'src/api/config';

import { get } from 'lodash';
import { ASSESSMENTS } from 'common/utils/EventTracker/utils/eventConstants';
import { RISK_LEVELS } from 'src/components/Screenings/constants';
import showRiskDisplayOption from 'src/components/Screenings/Detail/utils/showRiskDisplayOption';
import callOrLog from 'src/common/utils/callOrLog';
import getClientsDetailLink from 'src/common/utils/getClientsDetailLink';

const getBasename = ({ pathname, route, basename = '' }) => {
  const path = pathname.split(route).at(0);
  return `${basename}${path}${route}`;
};

const Screenings = ({
  location,
  currentEmployee,
  enums,
  providerId,
  groupId,
  isCaseManagerUser,
  crtb1121HideFieldInFormEnabled,
  hbh1509ClientsDrawerEnabled,
  scrn468ScreeningTimeTrackingEnabled,
  hbh1025ShowEligibilityAssessmentEnabled,
  scrn942ScreeningsResultsUpdatesEnabled,
}) => {
  const basename = getBasename({
    basename: location.basename,
    pathname: location.pathname,
    route: 'screenings/v2',
  });

  const trackEvent = useContext(TrackerContext);

  // TODO: This could be needed to get
  // state from AppScreenings. Note that
  // SupersetSearch, Referrals and ResourceList
  // all use useAppCreateReferralContext()
  // const { state: formSubmissionId } = useAppCreateReferralContext();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [contactData, setContactData] = useState(null);

  const handleContactClick = (contact) => {
    setContactData(contact);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      <AppScreenings
        source="web_app"
        basename={basename}
        appSettings={{
          env: {
            getAuthToken,
            coreUrl: env.CORE_API,
            employeeId: currentEmployee?.id,
            providerId: currentEmployee?.provider.id,
          },
          flags: {
            crtb1121HideFieldInFormEnabled,
            scrn468ScreeningTimeTrackingEnabled,
            hbh1509ClientsDrawerEnabled,
            hbh1025ShowEligibilityAssessmentEnabled,
            scrn942ScreeningsResultsUpdatesEnabled,
          },
          basename,
        }}
        adapters={{
          coreApi,
        }}
        hooks={{ ...apiHooks, useFindAllPlan }}
        appState={{
          enums,
          groupId,
          isCaseManagerUser,
          providerId,
          currentEmployee,
        }}
        utils={{
          fetchInsurances,
          generateClientData,
          getDobData,
          formatAddress,
          formatHouseholdCount,
          filterMilitaryInfo,
          formatDemographicInfo,
          findPrimaryOrFirst,
          dollarAmount,
          showRiskDisplayOption,
          callOrLog,
        }}
        callbacks={{
          notify: {
            error: (message) => Notifier.dispatch('error', message),
            success: (message) => Notifier.dispatch('success', message),
            warn: (message) => Notifier.dispatch('warning', message),
          },
          trackEvent,
          onSubmitSubmission: (formSubmissionId) => {
            browserHistory.replace(`/screenings/all/${formSubmissionId}`);
          },
          goToFacesheet: (contactId) => {
            browserHistory.push(`/facesheet/${contactId}`);
            // we have to change this for the current url
          },
          goBack: () => {
            browserHistory.goBack();
          },
          replace: (path) => {
            browserHistory.replace(path);
          },
          pushInHistory: (url) => {
            browserHistory.push(url);
          },
          onContactNameClick: (contact) => () => {
            handleContactClick(contact);
          },
          getClientsDetailLink,
        }}
        constants={{ RISK_LEVELS, ASSESSMENTS }}
        components={{
          MethodsOfContact,
          VoiceMailOk,
          StatusSelect,
          BackButton,
        }}
      />
      {contactData && hbh1509ClientsDrawerEnabled && (
        <ClientsDrawer
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          contact={{
            id: contactData.id,
            last_name_first_name: contactData.full_name || `${contactData.last_name}, ${contactData.first_name}`,
            ...contactData,
          }}
        />
      )}
    </>
  );
};

function mapStateToProps(state) {
  const enums = get(state, 'session.enums');
  const groupId = get(state, 'session.groupId');
  const isCaseManagerUser = isCaseManager(get(state, 'user'), groupId);
  const providerId = state.session.groupId;
  const currentEmployee = state.globalState.currentEmployee;
  const crtb1121HideFieldInFormEnabled = crtb1121HideFieldInForm(state);
  const scrn468ScreeningTimeTrackingEnabled = scrn468ScreeningTimeTracking(state);
  const hbh1509ClientsDrawerEnabled = hbh1509ClientsDrawer(state);
  const hbh1025ShowEligibilityAssessmentEnabled = hbh1025ShowEligibilityAssessmentTab(state);
  const scrn942ScreeningsResultsUpdatesEnabled = scrn942ScreeningsResultsUpdates(state);

  return {
    isCaseManager: isCaseManagerUser,
    enums,
    groupId,
    providerId,
    currentEmployee,
    crtb1121HideFieldInFormEnabled,
    hbh1509ClientsDrawerEnabled,
    scrn468ScreeningTimeTrackingEnabled,
    hbh1025ShowEligibilityAssessmentEnabled,
    scrn942ScreeningsResultsUpdatesEnabled,
  };
}

Screenings.propTypes = {
  location: PropTypes.string.isRequired,
  currentEmployee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    provider: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }).isRequired,
  providerId: PropTypes.string.isRequired,
  isCaseManagerUser: PropTypes.bool.isRequired,
  crtb1121HideFieldInFormEnabled: PropTypes.bool.isRequired,
  hbh1509ClientsDrawerEnabled: PropTypes.bool.isRequired,
  scrn468ScreeningTimeTrackingEnabled: PropTypes.bool.isRequired,
  hbh1025ShowEligibilityAssessmentEnabled: PropTypes.bool.isRequired,
  scrn942ScreeningsResultsUpdatesEnabled: PropTypes.bool.isRequired,
  groupId: PropTypes.string.isRequired,
  enums: PropTypes.object.isRequired,
};

Screenings.defaultProps = {};

export default connect(mapStateToProps)(Screenings);
